import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCountryByCurrency, type CountryOption } from "@/lib/countries";

const DEFAULT = getCountryByCurrency("BRL");

export function formatCurrencyWith(
  country: CountryOption,
  value: number,
  options: Intl.NumberFormatOptions = {},
): string {
  const n = Number.isFinite(value) ? value : 0;
  try {
    return n.toLocaleString(country.locale, {
      style: "currency",
      currency: country.currency,
      ...options,
    });
  } catch {
    return `${country.currencySymbol} ${n.toFixed(2)}`;
  }
}

type CurrencyCtx = {
  country: CountryOption;
  format: (value: number, options?: Intl.NumberFormatOptions) => string;
};

const CurrencyContext = createContext<CurrencyCtx>({
  country: DEFAULT,
  format: (v, o) => formatCurrencyWith(DEFAULT, v, o),
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [country, setCountry] = useState<CountryOption>(DEFAULT);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (mounted) setCountry(DEFAULT);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("currency")
        .eq("id", user.id)
        .maybeSingle();
      if (!mounted) return;
      if (data?.currency) setCountry(getCountryByCurrency(data.currency));
    };
    load();
    const { data: sub } = supabase.auth.onAuthStateChange(() => load());
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<CurrencyCtx>(
    () => ({
      country,
      format: (v, o) => formatCurrencyWith(country, v, o),
    }),
    [country],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyCtx {
  return useContext(CurrencyContext);
}

/** Backwards-compat: returns the current country only. */
export function useUserCurrency(): CountryOption {
  return useContext(CurrencyContext).country;
}